import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Users, FileText, Handshake, Info, Wrench } from "@phosphor-icons/react";
import TeamTab from "@/components/project/TeamTab";
import DocumentsTab from "@/components/project/DocumentsTab";
import BiddingTab from "@/components/project/BiddingTab";
import SubcontractorsTab from "@/components/project/SubcontractorsTab";

const fmtMoney = (n) => "$" + (n || 0).toLocaleString("en-US");

export default function ProjectWorkspace() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const modules = user?.modules || [];

  const load = () => api.get(`/projects/${projectId}`).then(({ data }) => setProject(data)).catch(() => navigate("/projects"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  if (!project) return <div className="p-8 overline text-muted-foreground animate-pulse">Loading project…</div>;

  return (
    <div>
      <div className="border-b border-border bg-card">
        <div className="p-6 lg:p-8">
          <button onClick={() => navigate("/projects")} data-testid="back-to-projects"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors duration-150 mb-4">
            <ArrowLeft size={16} /> All Projects
          </button>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="overline text-primary mb-1">{project.brand} · {project.number || "No #"}</div>
              <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">{project.name}</h1>
              <p className="text-muted-foreground mt-1">{project.address}</p>
            </div>
            <div className="text-right">
              <div className="font-heading font-black text-2xl tracking-tighter">{fmtMoney(project.estimated_budget)}</div>
              <div className="overline text-muted-foreground">Estimated Budget</div>
              <span className="inline-block mt-2 text-xs px-2.5 py-1 bg-primary text-white">{project.status}</span>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview" className="p-6 lg:p-8">
        <TabsList className="rounded-none bg-transparent border-b border-border w-full justify-start h-auto p-0 gap-1 mb-6">
          <TabTrig value="overview" icon={Info}>Overview</TabTrig>
          {modules.includes("team") && <TabTrig value="team" icon={Users}>Team</TabTrig>}
          {modules.includes("documents") && <TabTrig value="documents" icon={FileText}>Documents</TabTrig>}
          {modules.includes("bidding") && <TabTrig value="bidding" icon={Handshake}>GC Bidding</TabTrig>}
          {modules.includes("bidding") && <TabTrig value="subs" icon={Wrench}>Subcontractors</TabTrig>}
        </TabsList>

        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card border border-border p-6">
              <div className="overline text-muted-foreground mb-4">Project Information</div>
              <dl className="space-y-3 text-sm">
                <Row k="Project Name" v={project.name} />
                <Row k="Project Number" v={project.number || "—"} />
                <Row k="Brand" v={project.brand} />
                <Row k="Address" v={project.address || "—"} />
                <Row k="Start Date" v={project.start_date || "—"} />
                <Row k="Completion Date" v={project.completion_date || "—"} />
              </dl>
            </div>
            <div className="bg-card border border-border p-6">
              <div className="overline text-muted-foreground mb-4">Status Snapshot</div>
              <div className="grid grid-cols-3 gap-4 text-center mb-6">
                <Big n={project.team_count} l="Team" />
                <Big n={project.document_count} l="Documents" />
                <Big n={project.bid_count} l="Bids" />
              </div>
              <div className="overline text-muted-foreground mb-2">Description</div>
              <p className="text-sm text-muted-foreground">{project.description || "No description provided."}</p>
              {project.awarded_gc && (
                <div className="mt-6 border-t border-border pt-4">
                  <div className="overline text-primary mb-1">Awarded GC</div>
                  <div className="font-heading font-bold">{project.awarded_gc}</div>
                  <div className="text-sm text-muted-foreground">Contract: {fmtMoney(project.contract_value)}</div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {modules.includes("team") && <TabsContent value="team"><TeamTab projectId={projectId} onChange={load} /></TabsContent>}
        {modules.includes("documents") && <TabsContent value="documents"><DocumentsTab projectId={projectId} onChange={load} /></TabsContent>}
        {modules.includes("bidding") && <TabsContent value="bidding"><BiddingTab projectId={projectId} onChange={load} /></TabsContent>}
        {modules.includes("bidding") && <TabsContent value="subs"><SubcontractorsTab projectId={projectId} /></TabsContent>}
      </Tabs>
    </div>
  );
}

const TabTrig = ({ value, icon: Icon, children }) => (
  <TabsTrigger value={value} data-testid={`tab-${value}`}
    className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-2.5 gap-2">
    <Icon size={18} /> {children}
  </TabsTrigger>
);
const Row = ({ k, v }) => (
  <div className="flex justify-between border-b border-border pb-2"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium text-right">{v}</dd></div>
);
const Big = ({ n, l }) => (
  <div><div className="font-heading font-black text-2xl tracking-tighter">{n ?? 0}</div><div className="overline text-muted-foreground">{l}</div></div>
);
